import {
    useMutation,
    UseMutationOptions,
    useQueryClient,
    type UseMutateFunction,
} from '@tanstack/react-query'

import { SiteLink } from '@synapex/prisma/client'
import {
    CreateSiteAdminContactDto,
    CreateUTMDto,
    DeleteSiteLinkResponse,
    GenerateSiteLinkDto,
    MoveSiteLinksDto,
    MoveSiteLinksResponse,
    UpdateSiteLinkDto,
} from '@synapex/shared'
import { CamelIdObj } from '@synapex/tools/typescript'

import {
    LinksQueryKeyConstants,
    UTM_KEY,
} from '../invite-links-group/invite-links-group.query.constants'
import { SiteLinkWithAnalyticsType } from '../site-links-group/site-links-group.types'
import { siteLinksService, UtmReply } from './site-links.service'
import { siteLinkMany } from '@/true-fsd/data/link/react-query/key-fn'
import { useActiveWorkspace } from '@/true-fsd/data/advertiser-account'
import { UtmKey } from '@synapex/tools/utils'

export function useGenerateSiteLinkMutation(
    options?: UseMutationOptions<
        SiteLinkWithAnalyticsType,
        Error,
        GenerateSiteLinkDto
    >
) {
    const groupsQueryKey = LinksQueryKeyConstants.GroupsSites

    const queryClient = useQueryClient()

    return useMutation<SiteLinkWithAnalyticsType, Error, GenerateSiteLinkDto>({
        mutationFn: (data: GenerateSiteLinkDto) =>
            siteLinksService.generateSiteLink(data),
        ...options,
        onSuccess: (newInviteLink, variables, context) => {
            void queryClient.invalidateQueries({
                queryKey: siteLinkMany({
                    site_id: variables.siteId,
                }).queryKey,
            })
            void queryClient.invalidateQueries({
                queryKey: [groupsQueryKey, variables.siteId],
            })
            if (!newInviteLink.site_link_id) {
                throw new Error('Ошибка при создании инвайт-ссылки')
            }
            if (options?.onSuccess) {
                options.onSuccess(newInviteLink, variables, context)
            }
        },
        onError: (error, variables, context) => {
            console.log('error, variables, context', error, variables, context)
            if (options?.onError) {
                options.onError(error, variables, context)
            }
        },
        onSettled: (data, error, variables, context) => {
            if (options?.onSettled) {
                options.onSettled(data, error, variables, context)
            }
        },
    })
}

export function useUpdateSiteLinkMutation({
    siteId,
}: CamelIdObj<'site' | 'group'>) {
    const groupsQueryKey = LinksQueryKeyConstants.GroupsSites
    const siteLinksQueryKey = siteLinkMany({
        site_id: siteId,
    }).queryKey

    const queryClient = useQueryClient()

    return useMutation<
        SiteLink,
        Error,
        UpdateSiteLinkDto,
        { backup: SiteLinkWithAnalyticsType[] }
    >({
        mutationFn: (data: UpdateSiteLinkDto) =>
            siteLinksService.updateSiteLink(data),
        onMutate: async (dto) => {
            // Cancel any outgoing refetches
            // (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries({
                queryKey: siteLinksQueryKey,
            })

            const backup = queryClient.getQueryData(siteLinksQueryKey) ?? []

            // Optimistically update to the new value
            queryClient.setQueryData(siteLinksQueryKey, (old) =>
                old
                    ? old.map((link) => {
                          if (link.site_link_id === dto.siteLinkId) {
                              const update: Partial<SiteLinkWithAnalyticsType> =
                                  {}
                              if (dto.siteLinkStatus) {
                                  update.status = dto.siteLinkStatus
                              }

                              if (dto.name) {
                                  update.site_link_name = dto.name
                              }

                              if (dto.budget) {
                                  update.budget = dto.budget
                              }

                              if (Object.keys(update).length > 0) {
                                  return { ...link, ...update }
                              }
                          }

                          return link
                      })
                    : []
            )

            // Return a context object with the snapshotted value
            return { backup }
        },
        onSettled: (_updatedLink, _error, variables, _context) => {
            function getSearchParams(link?: string) {
                if (!link) return {}
                const url = new URL(link)

                return {
                    utm_source: url.searchParams.get('utm_source'),
                    utm_medium: url.searchParams.get('utm_medium'),
                    utm_campaign: url.searchParams.get('utm_campaign'),
                    utm_content: url.searchParams.get('utm_content'),
                }
            }

            const { utm_source, utm_medium, utm_campaign, utm_content } =
                getSearchParams(variables.utmLink)

            if (utm_source) {
                void queryClient.invalidateQueries({
                    queryKey: [LinksQueryKeyConstants.Sources, siteId],
                })
            }

            if (utm_medium) {
                void queryClient.invalidateQueries({
                    queryKey: [LinksQueryKeyConstants.Mediums, siteId],
                })
            }

            if (utm_campaign) {
                void queryClient.invalidateQueries({
                    queryKey: [LinksQueryKeyConstants.Campaigns, siteId],
                })
            }

            if (utm_content) {
                void queryClient.invalidateQueries({
                    queryKey: [LinksQueryKeyConstants.Contents, siteId],
                })
            }

            void queryClient.invalidateQueries({
                queryKey: [groupsQueryKey, siteId],
            })

            void queryClient.invalidateQueries({
                queryKey: siteLinksQueryKey,
            })
        },
        onError: (error, variables, context) => {
            console.error(
                `SiteLink ${JSON.stringify(variables)} updating error`
            )
            console.error(error)
            if (context) {
                queryClient.setQueryData(siteLinksQueryKey, context.backup)
            }
        },
    })
}

export type SiteLinkMutate = UseMutateFunction<
    SiteLink,
    Error,
    UpdateSiteLinkDto
>

export function useCreateUTM(
    key: UtmKey,
    siteId: number,
    options?: UseMutationOptions<UtmReply, Error, CreateUTMDto>
) {
    const queryClient = useQueryClient()

    return useMutation<UtmReply, Error, CreateUTMDto>({
        mutationFn: (dto: CreateUTMDto) => siteLinksService.createUTM(key, dto),
        ...options,
        onSuccess: (data, variables, context) => {
            void queryClient.invalidateQueries({
                queryKey: [UTM_KEY[key].query, siteId],
            })
            if (options?.onSuccess) {
                options.onSuccess(data, variables, context)
            }
        },
        onError: (error, variables, context) => {
            if (options?.onError) {
                options.onError(error, variables, context)
            }
        },
        onSettled: (data, error, variables, context) => {
            if (options?.onSettled) {
                options.onSettled(data, error, variables, context)
            }
        },
    })
}

export function useDeleteSiteLinkMutation({ siteId }: CamelIdObj<'site'>) {
    const queryClient = useQueryClient()

    const groupsQueryKey = LinksQueryKeyConstants.GroupsSites
    const siteLinksQueryKey = siteLinkMany({
        site_id: siteId,
    }).queryKey

    return useMutation<
        DeleteSiteLinkResponse,
        Error,
        number,
        { backup: SiteLinkWithAnalyticsType[] }
    >({
        mutationFn: (id: number) => siteLinksService.deleteSiteLink(id),
        onSuccess: (_data, variables, _context) => {
            console.log(`SiteLink ${variables} deleted successfully`)
        },
        onMutate: async (linkId) => {
            // Cancel any outgoing refetches
            // (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries({
                queryKey: siteLinksQueryKey,
            })

            const backup = queryClient.getQueryData(siteLinksQueryKey) ?? []

            // Optimistically update to the new value
            queryClient.setQueryData(siteLinksQueryKey, (old) =>
                old ? old.filter((link) => link.site_link_id !== linkId) : []
            )

            // Return a context object with the snapshotted value
            return { backup }
        },
        onSettled: () => {
            void queryClient.invalidateQueries({
                queryKey: siteLinksQueryKey,
            })
            void queryClient.invalidateQueries({
                queryKey: [groupsQueryKey, siteId],
            })
        },

        onError: (error, variables, context) => {
            console.error(`SiteLink ${variables} deleting error`)
            console.error(error)
            if (context) {
                queryClient.setQueryData(siteLinksQueryKey, context.backup)
            }
        },
    })
}

export function useMoveSiteLinkMutation(
    siteId: number,
    options?: UseMutationOptions<MoveSiteLinksResponse, Error, MoveSiteLinksDto>
) {
    const groupsQueryKey = LinksQueryKeyConstants.GroupsSites
    const siteLinksQueryKey = siteLinkMany({
        site_id: siteId,
    }).queryKey

    const queryClient = useQueryClient()

    return useMutation<MoveSiteLinksResponse, Error, MoveSiteLinksDto>({
        mutationFn: (moveDto: MoveSiteLinksDto) =>
            siteLinksService.moveSiteLinks(moveDto),
        ...options,
        onSuccess: (data, variables, context) => {
            void queryClient.invalidateQueries({
                queryKey: siteLinksQueryKey,
            })
            void queryClient.invalidateQueries({
                queryKey: [groupsQueryKey, siteId],
            })
            if (options?.onSuccess) {
                options.onSuccess(data, variables, context)
            }
        },
        onError: (error, variables, context) => {
            if (options?.onError) {
                options.onError(error, variables, context)
            }
        },
        onSettled: (data, error, variables, context) => {
            if (options?.onSettled) {
                options.onSettled(data, error, variables, context)
            }
        },
    })
}

export function useAddSiteAdminContactMutations(
    groupId: number,
    options?: UseMutationOptions<any, Error, CreateSiteAdminContactDto>
) {
    const queryClient = useQueryClient()

    const { activeAwd } = useActiveWorkspace()

    return useMutation<any, Error, CreateSiteAdminContactDto>({
        mutationFn: (dto: CreateSiteAdminContactDto) =>
            siteLinksService.addAdminContact(
                dto.siteLinkId,
                dto.telegramUsername,
                dto.shouldSelectAdmin
            ),
        ...options,
        onSuccess: (data, variables, context) => {
            if (activeAwd.workspace_type === 'site') {
                void queryClient.invalidateQueries({
                    queryKey: siteLinkMany({
                        site_id: activeAwd.workspace_id,
                    }).queryKey,
                })
            }
            if (options?.onSuccess) {
                options.onSuccess(data, variables, context)
            }
        },
        onError: (error, variables, context) => {
            if (options?.onError) {
                options.onError(error, variables, context)
            }
        },
        onSettled: (data, error, variables, context) => {
            if (options?.onSettled) {
                options.onSettled(data, error, variables, context)
            }
        },
    })
}

export function useDeleteSiteAdminContactMutations(
    groupId: number,
    options?: UseMutationOptions<any, Error, any>
) {
    const queryClient = useQueryClient()

    const { activeAwd } = useActiveWorkspace()

    return useMutation<any, Error, any>({
        mutationFn: ({ siteLinkId, adminContactId }) =>
            siteLinksService.deleteAdminContact(siteLinkId, adminContactId),
        ...options,
        onSuccess: (data, variables, context) => {
            if (activeAwd.workspace_type === 'site') {
                void queryClient.invalidateQueries({
                    queryKey: siteLinkMany({
                        site_id: activeAwd.workspace_id,
                    }).queryKey,
                })
            }
            if (options?.onSuccess) {
                options.onSuccess(data, variables, context)
            }
        },
        onError: (error, variables, context) => {
            if (options?.onError) {
                options.onError(error, variables, context)
            }
        },
        onSettled: (data, error, variables, context) => {
            if (options?.onSettled) {
                options.onSettled(data, error, variables, context)
            }
        },
    })
}
